import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import AnimeCard from "@/components/base/AnimeCard";
import NewsCard from "@/components/base/NewsCard";
import CharacterCard from "@/components/base/CharacterCard";
import EmptyState from "@/components/base/EmptyState";
import { searchContent, getAnimeById, getNewsById, getCharacterById } from "@/utils/content";
import { animeList } from "@/mocks/anime";

const typeTabs = [
  { key: "all", label: "全部" },
  { key: "anime", label: "动画推荐" },
  { key: "news", label: "动漫新闻" },
  { key: "character", label: "角色介绍" },
] as const;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const type = (searchParams.get("type") ?? "all") as "all" | "anime" | "news" | "character";
  const [draft, setDraft] = useState(keyword);

  useEffect(() => {
    setDraft(keyword);
  }, [keyword]);

  const results = useMemo(() => searchContent(keyword, type), [keyword, type]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams();
    if (draft.trim()) next.set("q", draft.trim());
    if (type !== "all") next.set("type", type);
    setSearchParams(next);
  };

  const switchType = (nextType: string) => {
    const next = new URLSearchParams(searchParams);
    if (nextType === "all") next.delete("type");
    else next.set("type", nextType);
    setSearchParams(next);
  };

  const recommendAnime = [...animeList].sort((a, b) => b.views - a.views).slice(0, 4);

  usePageMeta({
    title: keyword ? `搜索“${keyword}” - 风车动漫网` : "搜索中心 - 风车动漫网",
    description:
      "在风车动漫网搜索动画推荐、动漫新闻与角色介绍，支持关键词模糊匹配与分类筛选，快速定位你感兴趣的内容。",
    keywords: "动漫搜索,动画搜索,角色搜索,风车动漫网",
  });

  return (
    <Layout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
          <Breadcrumb items={[{ label: "搜索结果" }]} />
          <h1 className="mt-4 text-2xl font-bold text-foreground-950 md:text-3xl">搜索中心</h1>
          <p className="mt-1.5 text-sm text-foreground-600">
            输入关键词，搜索动画推荐、动漫新闻与角色介绍。
          </p>

          <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-400"></i>
              <input
                type="text"
                name="q"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="搜索动画 / 新闻 / 角色，例如：星海、治愈、剑"
                className="h-12 w-full rounded-full border border-background-300 bg-background-50 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="h-12 cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-7 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            {typeTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => switchType(tab.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  type === tab.key
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-600 hover:text-primary-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6">
        <p className="mb-5 text-sm text-foreground-600">
          {keyword ? (
            <>
              关键词 <span className="font-semibold text-foreground-900">“{keyword}”</span> 共找到{" "}
              <span className="font-semibold text-primary-700">{results.length}</span> 条结果
            </>
          ) : (
            "输入关键词开始搜索，浏览全部内容可从下方推荐进入。"
          )}
        </p>

        {keyword && results.length === 0 ? (
          <EmptyState
            icon="ri-search-eye-line"
            title="没有找到相关内容"
            description="换个关键词试试，或从下方的热门动画开始浏览。"
          />
        ) : (
          results.length > 0 && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-4">
              {results.map((result) => {
                if (result.type === "anime") {
                  const anime = getAnimeById(result.id);
                  return anime ? <AnimeCard key={result.id} anime={anime} /> : null;
                }
                if (result.type === "news") {
                  const news = getNewsById(result.id);
                  return news ? <NewsCard key={result.id} news={news} /> : null;
                }
                const character = getCharacterById(result.id);
                return character ? <CharacterCard key={result.id} character={character} /> : null;
              })}
            </div>
          )
        )}

        {(results.length === 0 || !keyword) && (
          <section className="mt-12">
            <h2 className="mb-5 text-lg font-bold text-foreground-950">热门动画推荐</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
              {recommendAnime.map((anime) => (
                <AnimeCard key={anime.id} anime={anime} />
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/category/recommend"
                className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-primary-100 px-4 py-2 text-sm text-primary-800 transition-colors hover:bg-primary-200"
              >
                <i className="ri-film-line"></i>
                动画推荐
              </Link>
              <Link
                to="/category/news"
                className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-secondary-100 px-4 py-2 text-sm text-secondary-800 transition-colors hover:bg-secondary-200"
              >
                <i className="ri-newspaper-line"></i>
                动漫新闻
              </Link>
              <Link
                to="/category/characters"
                className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-accent-100 px-4 py-2 text-sm text-accent-800 transition-colors hover:bg-accent-200"
              >
                <i className="ri-user-star-line"></i>
                角色介绍
              </Link>
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}